import Markdown, { Components } from 'react-markdown';
import '../../legacy-components/markdown-to-jsx/markdown-message.css';

import remarkGfm from 'remark-gfm';
import { ErrorBoundary } from '../../legacy-components/markdown-to-jsx/ErrorBoundary';
import { ComponentProps, useMemo, useState } from 'react';
import { useTextAnimation } from './useTextAnimation';
import { CustomCodeBlock } from './CustomCodeBlock';
import { useTranslation } from 'react-i18next';

const Fallback = () => {
  const { t } = useTranslation('designSystem');
  return <h2>{t('somethingWentWrong')}</h2>;
};

export const CUSTOM_COMPONENTS: Components = {
  code: CustomCodeBlock as Components['code'],
  a: (({ node, ...props }: any) => (
    <a target="_blank" rel="noopener noreferrer" {...props} />
  )) as any,
};

export const ChatMarkdown = ({
  content,
  wrapperClassName,
  textAnimation,
  showCursor = false,
  customComponents,
  ...markdownProps
}: {
  content?: string;
  wrapperClassName?: string;
  showCursor?: boolean;
  textAnimation?: {
    animate?: boolean;
    duration?: number;
    letterPerDuration?: number;
  };
  customComponents?: Components;
} & Partial<ComponentProps<typeof Markdown>>) => {
  const [resetKey, setResetKey] = useState(1);

  const { text: animationCompletedText } = useTextAnimation({
    text: content || '',
    enabled: textAnimation?.animate,
    duration: textAnimation?.duration,
    letterPerDuration: textAnimation?.letterPerDuration,
  });

  const contentToRender = textAnimation?.animate
    ? animationCompletedText
    : content;

  const contentWithCursor = showCursor
    ? `${contentToRender} ●`
    : contentToRender;

  const components = useMemo(() => {
    return {
      ...CUSTOM_COMPONENTS,
      ...customComponents,
    };
  }, [customComponents]);

  return (
    <ErrorBoundary
      fallback={<Fallback />}
      setResetKey={setResetKey}
      key={resetKey}
    >
      <div
        className={`markdown-message text-base break-words text-gray-700 dark:text-gray-300 ${wrapperClassName}`}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={components}
          {...markdownProps}
        >
          {contentWithCursor}
        </Markdown>
      </div>
    </ErrorBoundary>
  );
};
